import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Button } from "primereact/button";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { IUniversalDatasetTableConfig } from "components/dashboard/universal-dataset-table/UniversalDatasetTableTypes";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { T } from "services/lang/LanguageService";
import { Dialog } from "primereact/dialog";
import React, { useState } from "react";
import useDashboardStore from "store/dashboard-store";
import DraggableItem, { ReorderType } from "./DraggableItem";
import { onDragEndSplice, tabUpdateInDashboard } from "../../../utils";

export interface ComponentReorderConfig {
  type?: ReorderType;
  isVisible: boolean;
}

interface ReorderProps {
  setReorderDialogVisibility: (componentReorderConfig: ComponentReorderConfig) => void;
  reorderDialogVisibility: boolean;
  reorderType: ReorderType;
  universalTableConfig?: IUniversalDatasetTableConfig;
  onUniversalTableConfigChange?: (config: IUniversalDatasetTableConfig) => void;
  onChange?: (tabId: number) => void;
  group?: DashboardComponentGroup;
  tabId?: number;
}

const Reorder = (props: ReorderProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const [reorderedTabs, setReorderedTabs] = useState<DashboardTabModel[]>(dashboard?.dashboardTabsJs || []);

  const onDragEnd = (result: any) => {
    if (!result.destination || !dashboard) return;

    switch (props.reorderType) {
      case ReorderType.TABS:
        handleReorderTabs(result);
        break;
      case ReorderType.COMPONENTS:
        handleReorderComponents(result);
        break;
      case ReorderType.GROUP_HEADER_COMPONENTS:
        handleReorderComponents(result, true);
        break;
      case ReorderType.GROUPS:
        handleReorderGroups(result);
        break;
      case ReorderType.UNIVERSAL_TABLE_COLUMNS:
        handleReorderColumns(result);
        break;
      default:
        break;
    }
  };

  const handleReorderTabs = (result: any) => {
    if (reorderedTabs) {
      const currReorderedTabs = Array.from(reorderedTabs);
      const [removed] = currReorderedTabs.splice(result.source.index, 1);
      currReorderedTabs.splice(result.destination.index, 0, removed);

      setReorderedTabs(currReorderedTabs);
    }
  };

  const handleReorderComponents = (result: any, isGroupHeader?: boolean) => {
    if (props.tabId == null || props.group?.id == null || !dashboard) return;

    const updatedComponentGroups = reorderedTabs[props.tabId].componentGroups.map(
      (currentGroup: DashboardComponentGroup) => {
        if (currentGroup.id === props.group?.id) {
          if (isGroupHeader) {
            const reorderedComponents = onDragEndSplice(result, currentGroup.headerConfig?.components || []);
            return {
              ...currentGroup,
              headerConfig: { ...currentGroup.headerConfig, components: reorderedComponents },
            };
          }
          const reorderedComponents = onDragEndSplice(result, currentGroup.components || []);
          return { ...currentGroup, components: reorderedComponents };
        }
        return currentGroup;
      }
    );
    setReorderedTabs(
      tabUpdateInDashboard(reorderedTabs, props.tabId, {
        componentGroups: updatedComponentGroups,
      })
    );
  };

  const handleReorderGroups = (result: any) => {
    if (props.tabId == null || !dashboard) return;

    const reorderedGroups = onDragEndSplice(result, reorderedTabs[props.tabId].componentGroups);

    setReorderedTabs(
      reorderedTabs.map((tab, index) =>
        index === props.tabId ? { ...tab, componentGroups: reorderedGroups } : tab
      )
    );
  };

  const handleReorderColumns = (result: any) => {
    if (props.universalTableConfig?.columnsOptions) {
      const reorderedColumnsOptions = onDragEndSplice(result, props.universalTableConfig.columnsOptions);

      const reorderedRows = props.universalTableConfig.rows.map(row => {
        const reorderedRowColumns = onDragEndSplice(result, row.columns);
        return { ...row, columns: reorderedRowColumns };
      });

      props.onUniversalTableConfigChange?.({
        ...props.universalTableConfig,
        columnsOptions: reorderedColumnsOptions,
        rows: reorderedRows,
      });
    }
  };

  const reorderTypeHeaders = {
    [ReorderType.TABS]: T("label.reorder-tabs"),
    [ReorderType.GROUPS]: T("label.reorder-groups"),
    [ReorderType.COMPONENTS]: T("label.reorder-components"),
    [ReorderType.GROUP_HEADER_COMPONENTS]: T("label.reorder-group-header-components"),
    [ReorderType.UNIVERSAL_TABLE_COLUMNS]: T("label.reorder-columns"),
    [ReorderType.DD_MENU_ITEMS]: T("label.reorder-dd-menu-items"),
  };

  const getDroppableContent = () => {
    switch (props.reorderType) {
      case ReorderType.TABS:
        return reorderedTabs.map((tab, index) => (
          <Draggable key={tab.uuid} draggableId={tab.uuid} index={index}>
            {provided => <DraggableItem item={tab} provided={provided} type={ReorderType.TABS} />}
          </Draggable>
        ));

      case ReorderType.COMPONENTS:
        return (
          props.tabId != null &&
          reorderedTabs[props.tabId]?.componentGroups
            .find(g => g.id === props.group?.id)
            ?.components?.map((component, index) => (
              <Draggable key={component.id} draggableId={component.id} index={index}>
                {provided => (
                  <DraggableItem item={component} provided={provided} type={ReorderType.COMPONENTS} />
                )}
              </Draggable>
            ))
        );

      case ReorderType.GROUP_HEADER_COMPONENTS:
        return (
          props.tabId != null &&
          reorderedTabs[props.tabId]?.componentGroups
            .find(g => g.id === props.group?.id)
            ?.headerConfig?.components?.map((component, index) => (
              <Draggable key={component.id} draggableId={component.id} index={index}>
                {provided => (
                  <DraggableItem item={component} provided={provided} type={ReorderType.COMPONENTS} />
                )}
              </Draggable>
            ))
        );

      case ReorderType.GROUPS:
        return (
          props.tabId != null &&
          reorderedTabs[props.tabId].componentGroups.map((group, index) => (
            <Draggable key={group.id} draggableId={group.id} index={index}>
              {provided => <DraggableItem item={group} provided={provided} type={ReorderType.GROUPS} />}
            </Draggable>
          ))
        );

      case ReorderType.UNIVERSAL_TABLE_COLUMNS:
        return props.universalTableConfig?.columnsOptions.map((column, index) => (
          <Draggable key={column.id} draggableId={column.id} index={index}>
            {provided => (
              <DraggableItem item={column} provided={provided} type={ReorderType.UNIVERSAL_TABLE_COLUMNS} />
            )}
          </Draggable>
        ));

      default:
        return null;
    }
  };

  const handleSave = () => {
    if (dashboard && reorderedTabs) {
      setDashboard({ ...dashboard, dashboardTabsJs: reorderedTabs });
      if (props.reorderType === ReorderType.TABS) {
        props.onChange?.(0);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    props.setReorderDialogVisibility({
      type: undefined,
      isVisible: false,
    });
    setReorderedTabs([]);
  };

  const footerTemplate = () => (
    <div className="flex justify-content-end gap-2 mt-3">
      <Button
        label={T("action.cancel")}
        icon="pi pi-times"
        className="component-editor-button icon-red"
        onClick={handleClose}
      />
      <Button
        label={T("action.save")}
        icon="pi pi-check"
        className="component-editor-button icon-green"
        onClick={handleSave}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={reorderTypeHeaders[props.reorderType]}
      className="w-30rem"
      style={{ maxHeight: "60rem" }}
      footer={footerTemplate()}
      modal={false}
      resizable
      draggable
      visible={props.reorderDialogVisibility}
      onHide={() =>
        props.setReorderDialogVisibility({
          type: undefined,
          isVisible: false,
        })
      }
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`${props.group?.id || "droppable"}`} direction="vertical">
          {providedDrop => (
            <div {...providedDrop.droppableProps} ref={providedDrop.innerRef} className="tabsFlex column">
              {getDroppableContent()}
              {providedDrop.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Dialog>
  );
};

export default Reorder;
